import { type FC, useState } from 'react';
import styled from 'styled-components';

import { QueryState } from '@/api/components/QueryState';
import {
  formatDateArgument,
  useFetchAssetPerformanceQuery,
} from '@/api/performanceData/performanceDataApi';
import { isNotReady } from '@/api/utils/isNotReady';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';

import { AssetAllocationViewModeChips } from '../components/AssetAllocationViewModeChips';
import { AssetAllocationViewMode } from '../types/AssetAllocationViewMode';
import { AssetAllocationTable } from './AssetAllocationTable';

interface Props {
  portfolioGroupShortName: string;
  date?: Date;
  defaultViewMode?: AssetAllocationViewMode;
}

const today = new Date();

export const AssetAllocationTableWidget: FC<Props> = ({
  portfolioGroupShortName,
  date,
  defaultViewMode,
}) => {
  const texts = useTexts();

  const assetPerformanceQuery = useFetchAssetPerformanceQuery({
    portfolioGroup: portfolioGroupShortName,
    to: formatDateArgument(date ?? today),
  });

  const [selectedViewMode, setSelectedViewMode] =
    useState<AssetAllocationViewMode>(
      defaultViewMode ?? AssetAllocationViewMode.Holdings,
    );

  return (
    <WithGap
      data-test={`widget-asset-allocation-table-${portfolioGroupShortName}`}
    >
      <WithGap gap="8" direction="row">
        <h2>{texts.widgets.assetAllocation.table.title}</h2>

        <ConnectPopover
          id="asset-allocation-table"
          title={texts.widgets.assetAllocation.table.help.title}
          description={texts.widgets.assetAllocation.table.help.description}
        />
      </WithGap>

      <FilterContainer>
        <AssetAllocationViewModeChips
          checked={selectedViewMode}
          onChange={setSelectedViewMode}
        ></AssetAllocationViewModeChips>
      </FilterContainer>
      {isNotReady(assetPerformanceQuery) ? (
        <QueryState query={assetPerformanceQuery} />
      ) : (
        <AssetAllocationTable
          viewMode={selectedViewMode}
          assetPerformance={assetPerformanceQuery.data!}
        />
      )}
    </WithGap>
  );
};

const FilterContainer = styled.div`
  display: flex;
  justify-content: end;
`;
